import { useState, useMemo, useEffect } from 'react'
import { useUser } from 'app/User'
import { RfSpongeCountPanel } from 'components/Consumables/RfSpongeCountPanel'
import {
  useDeleteSurgeryAssetsByIds,
  useDeleteConsumableAssetsByIds,
  useGetSurgeryQuery,
} from 'lib/apollo/hooks'
import {
  deriveAssetDetailsFromAssetData,
  groupScanStatuses,
  getCountFromScans,
} from 'lib/utils/data'
import { parseAssetIdentifiers } from 'lib/utils/ParseAssetIdentifiers/parseAssetIdentifiers'
import { DispositionedAssetDetails } from '../DispositionedAssetDetails/DispositionedAssetDetails'
import { AddMissingFieldsProps, StoredAssetProps } from './StoredAsset.types'
import { EditAssetButton } from '../EditAssetButton/EditAssetButton'
import { useEditDocumentedListContext } from 'lib/context/EditDocumentedListContext/EditDocumentedListContext'
import { isDatePast } from 'lib/utils/isDatePast'
import { Grid, Typography } from '@mui/material'
import { WASTED_REASON_LIST } from 'common/disposition'
import { Surgery } from 'common/types'
import { AssetData } from 'common/types'
import { v4 as uuid } from 'uuid'
import { useReturnDTMScrewToTray } from 'hooks/useReturnDTMScrewToTray'
import formatFraction from 'lib/utils/formatFraction'

export const useStoredAssetLogic = ({
  assetData,
  surgeryId,
  isProcedureSubmitted,
  printMode,
}: StoredAssetProps) => {
  // hooks
  const { user, isNurseAdmin, isNurse } = useUser()
  const { isSavingEditedAsset } = useEditDocumentedListContext()
  const { returnScrewToTray } = useReturnDTMScrewToTray()

  const { loading: surgeryLoading, data: surgeryData } = useGetSurgeryQuery(
    surgeryId,
    {
      disablePolling: true,
    }
  )

  const surgery: Surgery = surgeryData?.getSurgery

  const isAutopopulated = surgeryLoading
    ? undefined
    : surgery?.autoPopulatedConsumables?.some(
        (item) => item.bidProductId === assetData.bidAssetId
      ) || false

  const addendumScanCount =
    assetData?.scans.filter((scan) => scan.status?.isAddedByAddendum).length ||
    0

  const assetDetails = deriveAssetDetailsFromAssetData(assetData)
  const {
    assetDescription,
    sizeText,
    deviceCount,
    scans,
    secondaryDeviceIdType,
    accurateDeviceCount,
    deviceCountCorrectiveActionTaken,
    expirationDate,
    serialNumber,
    catalogNumber,
    lotBatch,
    deviceId,
    assetTray,
    versionModelNumber,
    isManualAddition,
    _id,
    bidAssetId,
    parentCompany,
  } = assetDetails

  const [deleteSurgeryAssetsByIds, deleteSurgeryAssetsByIdsMutation] =
    useDeleteSurgeryAssetsByIds({ surgeryId })
  const [deleteConsumableAssetsByIds, deleteConsumableAssetsByIdsMutation] =
    useDeleteConsumableAssetsByIds({
      surgeryId,
    })

  // states
  const [toBeDeleted, setToBeDeleted] = useState<string[]>([])
  const [reviewingMedia, setReviewingMedia] = useState(false)
  const [mediaAssetId] = useState(assetData?.scans[0]._id)
  const [longestLabel, setLongestLabel] = useState(0)

  // constants
  const groupedStatuses = useMemo(() => groupScanStatuses(scans), [scans])
  const assetType = assetData.scans[0].assetType
  const fileNames = scans
    ?.flatMap((scan) => scan.media)
    .filter((media) => media !== undefined) as string[]

  const {
    isMultipackConsumable,
    isRfMultipackConsumable,
    isMultipackHardware,
    isSpongeConsumable,
  } = parseAssetIdentifiers({
    deviceDescription: assetDescription,
    deviceCount,
    idType: secondaryDeviceIdType,
    assetType: assetType,
  })

  const consumableScans = useMemo(
    () => scans.filter((scan) => scan.assetType === 'consumable'),
    [scans]
  )

  const consumablesPacksCount = assetData.packsUsed

  const associatedScans = useMemo(
    () => scans.filter((scan) => scan.implantStatus === 'ASSOCIATED_ASSET'),
    [scans]
  )
  const associatedCount = getCountFromScans(associatedScans)

  const hasApproved = useMemo(
    () => 'APPROVED' in groupedStatuses,
    [groupedStatuses]
  )

  const isApproved = useMemo(
    () => hasApproved && Object.keys(groupedStatuses).length === 1,
    [groupedStatuses, hasApproved]
  )

  const status = assetData.scans[0].status
  const isRepScan = Boolean(status.bidCompanyId)
  const canDelete =
    !isProcedureSubmitted &&
    !isApproved &&
    (isNurse || isNurseAdmin || (isRepScan && status.userId === user?.id))

  const isConsumable = assetType === 'consumable'
  const packsUsed = isMultipackHardware ? assetData.packsUsed ?? 1 : 1
  let unusedCount = isMultipackHardware
    ? deviceCount * packsUsed - assetData.total
    : 0

  const isDTMScrew = scans.some(
    (scan) =>
      scan.isDTMScrew &&
      assetData.scans.some((assetScan) => assetScan._id === scan._id)
  )

  const isExpired = isDatePast({ date: expirationDate })?.isDateExpired

  // handles
  const handleDelete = async (deleteCount: number) => {
    if (isConsumable) {
      try {
        const result = await deleteConsumableAssetsByIds({
          variables: {
            count: deleteCount,
            assetIds: toBeDeleted.slice(0, deleteCount),
            isMultipack: isMultipackConsumable,
          },
        })

        if (result.data?.deleteConsumableAssetsByIds?.success) {
          setToBeDeleted([])
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      // For non-consumables, check if any are DTM screws before deleting
      const scansToDelete = assetData.scans.filter((scan) =>
        toBeDeleted.slice(0, deleteCount).includes(scan._id)
      )

      // Return any DTM screws to their trays
      for (const scan of scansToDelete) {
        if (scan.isDTMScrew && scan.dtmScrewData) {
          await returnScrewToTray(scan)
        }
      }

      await deleteSurgeryAssetsByIds({
        variables: {
          surgeryId,
          assetIds: toBeDeleted.slice(0, deleteCount),
        },
      }).finally(() => {
        setToBeDeleted([])
      })
    }
  }

  const consumablesCountLabel = () => {
    const isConsumable = assetData.scans[0]?.assetType === 'consumable'
    const deviceCategoryDetails = assetData.scans[0]?.deviceCategoryDetails

    const consumablesCount = isConsumable ? assetData.packsUsed : 0

    const rfSpongeCount: number =
      deviceCategoryDetails === 'rfSponge'
        ? assetData.scans[0]?.spongeCount || 0
        : 0

    const spongeCount: number =
      deviceCategoryDetails === 'sponge'
        ? assetData.scans[0]?.spongeCount || 0
        : 0

    let label = 'Used'

    const handleDelete = canDelete
      ? () => {
          const ids = consumableScans.map((scan) => scan._id)
          setToBeDeleted(ids)
        }
      : undefined

    const getValue = () => {
      if (assetData.scans[0]?.measurement) {
        const [numerator, denominator] =
          assetData.scans[0].measurement?.unit?.split('/')
        if (numerator && denominator) {
          return formatFraction(Number(denominator), Number(numerator))
        } else {
          return assetData.scans[0].measurement?.unit
        }
      } else if (
        isConsumable &&
        assetData.scans[0].deviceCategoryDetails === null
      ) {
        return assetData.total + (assetData?.wasted ?? 0)
      } else if (assetData.packsUsed) {
        return assetData.packsUsed
      } else {
        return 0
      }
    }

    if (rfSpongeCount) {
      return (
        <RfSpongeCountPanel
          isAutopopulated={isAutopopulated}
          printMode={printMode}
          label={label}
          spongeCount={rfSpongeCount * consumablesCount}
          value={assetData.packsUsed}
          isProcedureSubmitted={isProcedureSubmitted}
          handleDelete={handleDelete}
          isLoading={
            deleteConsumableAssetsByIdsMutation.loading ||
            isSavingEditedAsset(_id)
          }
          isManualAddition={isManualAddition}
          assetGroup={assetData}
          assetTray={assetTray}
          surgeryId={surgeryId}
        />
      )
    } else if (spongeCount > 0 && !isAutopopulated) {
      return (
        <DispositionedAssetDetails
          value={assetData.packsUsed}
          label={label}
          handleDelete={handleDelete}
          isLoading={
            deleteConsumableAssetsByIdsMutation.loading ||
            isSavingEditedAsset(_id)
          }
          editComponent={
            !printMode ? (
              <EditAssetButton
                assetGroup={assetData}
                isSponge={true}
                isManualAddition={isManualAddition}
                bidAssetId={bidAssetId}
                assetgroupId={_id}
                isProcedureSubmitted={isProcedureSubmitted}
                surgeryId={surgeryId}
                assetTray={assetTray}
                consumableAssetGroup={assetData}
                isMultipack={false}
              />
            ) : null
          }
        />
      )
    } else if (isConsumable) {
      return (
        <DispositionedAssetDetails
          value={getValue()}
          label={label}
          handleDelete={handleDelete}
          isLoading={
            deleteConsumableAssetsByIdsMutation.loading ||
            isSavingEditedAsset(_id)
          }
          editComponent={
            !printMode ? (
              <EditAssetButton
                assetGroup={assetData}
                isSponge={false}
                singleConsumablesCount={consumablesCount}
                isManualAddition={isManualAddition}
                bidAssetId={bidAssetId}
                assetgroupId={_id}
                isProcedureSubmitted={isProcedureSubmitted}
                surgeryId={surgeryId}
                assetTray={assetTray}
                consumableAssetGroup={assetData}
                isMultipack={false}
                scanIds={
                  consumableScans[0].measurement
                    ? [consumableScans[0]._id]
                    : undefined
                }
                scansArray={
                  consumableScans[0].measurement ? consumableScans : undefined
                }
              />
            ) : null
          }
        >
          {((isConsumable && assetData.wastedReason) ||
            isMultipackConsumable) && (
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              sx={{ mt: 0 }}
            >
              {assetData.total ? (
                <Grid item sx={{ mt: 1 }}>
                  <Grid item>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'black',
                        ml: 1,
                        mr: 1,
                        textTransform: 'capitalize',
                        opacity: 0.8,
                      }}
                    >
                      {`Items used: ${assetData.total}`}
                    </Typography>
                  </Grid>
                </Grid>
              ) : null}
              {assetData.wastedReason && (
                <Grid item sx={{ mt: 1 }}>
                  <Grid item>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'black',
                        ml: 1,
                        mr: 1,
                        textTransform: 'capitalize',
                        opacity: 0.8,
                      }}
                    >
                      {`Items wasted: ${assetData.wasted} - ${
                        WASTED_REASON_LIST.find(
                          (reason) => reason.id === assetData.wastedReason
                        )?.name
                      }`}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
        </DispositionedAssetDetails>
      )
    } else {
      return null
    }
  }

  useMemo(() => {
    setLongestLabel(0)
  }, [assetData.scans])

  const [addMissingFields, setAddMissingFields] =
    useState<AddMissingFieldsProps>({
      id: uuid(),
      open: false,
    })
  const handleAddMissingFields = (
    fields: string[],
    assetId?: AssetData['_id']
  ) => {
    setAddMissingFields({
      open: true,
      assetId,
      fields,
    })
  }

  const handleCloseMissingFieldsModal = () => {
    setAddMissingFields({
      open: false,
      id: uuid(),
    })
  }

  useEffect(() => {
    if (!addMissingFields.open) {
      setAddMissingFields({
        open: false,
        id: uuid(),
      })
    }
  }, [addMissingFields.open])

  return {
    assetData,
    assetType,
    assetTray,
    associatedCount,
    canDelete,
    consumablesCountLabel,
    deleteSurgeryAssetsByIdsMutation,
    deviceCountCorrectiveActionTaken,
    expirationDate,
    groupedStatuses,
    isConsumable,
    isDTMScrew,
    isMultipackConsumable,
    isMultipackHardware,
    isRfMultipackConsumable,
    isSpongeConsumable,
    mediaAssetId,
    reviewingMedia,
    scans,
    serialNumber,
    setReviewingMedia,
    sizeText,
    toBeDeleted,
    unusedCount,
    versionModelNumber,
    handleDelete,
    deleteConsumableAssetsByIdsMutation,
    setToBeDeleted,
    isApproved,
    accurateDeviceCount,
    assetDescription,
    deviceId,
    catalogNumber,
    lotBatch,
    deviceCount,
    associatedScans,
    hasApproved,
    fileNames,
    isExpired,
    parentCompany,
    longestLabel,
    setLongestLabel,
    isAutopopulated,
    addendumScanCount,
    consumablesPacksCount,
    handleAddMissingFields,
    addMissingFields,
    handleCloseMissingFieldsModal,
  }
}
